import { Order } from '@baemingo/order-status-events/dist/types';
import useConfirm from './useConfirm';
import { LocalItem } from '~Types';
import { useDispatch, useI18n, useKitchenMessage, useLogger, useSelector, useWaste } from '~Hooks';
import * as actions from '~actions';
import { CreatePOSOrder } from '~reducers';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useEntryFinder } from '~Components/EntryFinder';
import { extractServiceChargePercentage, isServiceChargeItem } from '~Utils/serviceCharge';
import { parseParkedOrderName } from '~Scenes/ParkedOrders/utils';
import { useReportsApi } from '~Components/ReportsUniverse';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import { useHistory, useLocation } from 'react-router-dom';
import { useStorage } from '~Components/Storage';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';

// Could not find a good type for this, can be replaced if there is one
export type OrderWithItems = Order &
  CreatePOSOrder & { items: LocalItem[]; orderEasyId?: string; name?: string; userId?: string };

const useOrderParking = () => {
  const { i18n } = useI18n();
  const { pathname } = useLocation();
  const logger = useLogger('parked-orders');
  const dispatch = useDispatch();
  const tableApi = useTableApi();
  const reportsApi = useReportsApi();
  const tableUniverse = useTableUniverse();
  const history = useHistory();
  const waste = useWaste();
  const [storage] = useStorage();

  const findEntryByItemId = useEntryFinder();
  const sendKitchenMessage = useKitchenMessage();

  const { confirm } = useConfirm();

  const country = useSelector((state) => state.country);
  const venueId = useSelector((state) => state.venueId);
  const companyStaff = useSelector((state) => state.companyStaff?.staffs);
  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff?.id : false,
  );

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);

  /**
   * Parks a given order.
   * We create a new cheque with the items with the tableId 'parked-order'
   * @param order
   * @returns The chequeId of the parked order
   */
  const park = async <Type>(order: OrderWithItems & Type): Promise<string | undefined> => {
    if (!order) {
      return;
    }

    /* Parked orders are cheques with tableId === PARKED_ORDER_TABLE_ID which is const variable */
    const chequeId = tableApi.createCheque(PARKED_ORDER_TABLE_ID);

    /* Ask if the order should print a kitchen ticket as it is parked & save if it was printed or not
     * Spain has their own version of this in src/Scenes/POS/Components/ParkedOrderPrintsModal/index.tsx
     * - Skip the default confirm dialog for Spain entirely as they use ParkedOrderPrintsModal
     */

    const kitchenTicketPrintedOnPark =
      country === 'ES'
        ? false
        : await confirm(i18n('ParkOrder.BongPrompt'));

    /* Print kitchen ticket on prompt confirm */
    if (kitchenTicketPrintedOnPark) {
      dispatch({
        type: actions.SEND_TABLE_ITEMS,
        items: order.items.filter((item) => !item.isMessage),
        order: {
          ...order,
          orderNumber: order.orderEasyId,
          estimatedFinishTime: order.desiredTime || null,
        },
        serviceType: order.serviceType!,
        messages: order.items.filter((item) => item.isMessage),
        origin: order.origin,
      });

      tableApi.markPrinted(chequeId);
    }

    /* We need to save serviceType, the name of the device it was parked on & desiredTime (if available). Not a clean solution but this to avoid touching tableApi */
    const nameOfCheque = `[orderEasyId:${order.orderEasyId},serviceType:${order.serviceType},device:${storage.taxComplianceDeviceName},desiredTime:${order.desiredTime}] ${order?.name}`;

    tableApi.setChequeName(chequeId, nameOfCheque);

    if (order.userId) {
      tableApi.setChequeUser(chequeId, order?.userId);
    }

    for (const item of order.items) {
      const entry = findEntryByItemId(item.itemId);

      if (isServiceChargeItem(item.title)) {
        const percentage = extractServiceChargePercentage(item.title);

        tableApi.addServiceCharge({
          chequeId,
          entryId: entry.id,
          itemId: item.itemId,
          categoryId: item.categoryId,
          percentage: percentage,
          vatRate: item.vatRate,
        });
      } else {
        let amount;

        if (item.price) {
          amount = item.price;
        } else {
          amount = entry.price + item.variations.reduce((acc, v) => acc + v.price, 0);
        }

        tableApi.addItem(
          {
            ...item,
            discount: item.discount ?? 0,
            amount,
            fraction: 1,
            entryId: entry.id,
          },
          chequeId,
        );
      }
    }

    return chequeId;
  };

  /**
   * Edits the cheques name. We take into consideration that we have other data in that field.
   * @param chequeId
   * @param name - The name we want to change to (inputted by the cashier)
   * @returns The formatted new name of the parked order (with serviceType and deviceName)
   */
  const editName = (chequeId: string, name: string): string => {
    const cheque = tableUniverse.cheques.find((c) => c.id === chequeId);
    const parsedOrder = parseParkedOrderName(cheque?.name ?? '');

    const nameOfCheque = `[orderEasyId:${parsedOrder.orderEasyId},serviceType:${parsedOrder.serviceType},device:${parsedOrder.deviceName},desiredTime:${parsedOrder.desiredTime}] ${name}`;

    tableApi.setChequeName(chequeId, nameOfCheque);

    return nameOfCheque;
  };

  /**
   * Removes a parked order. I.e removes the selected cheque
   * If the parked order was printed on park, we send a VOID_CHEQUE message to the kitchen
   * @param chequeId The chequeId of the parked order
   * @param reason The reason for removing the parked order (entered by the staff)
   * @param chequeWasSentToPOS If the parked order was sent to POS, we don't send a VOID_CHEQUE message to the kitchen
   * @param commentOnWaste Comment if the removal is registered as waste
   */
  const remove = (
    chequeId: string,
    reason: string,
    chequeWasSentToPOS?: boolean,
    commentOnWaste?: string,
  ) => {
    const cheque = tableUniverse.cheques.find((c) => c.id === chequeId);
    const itemsOnChequeForDeletion = tableUniverse.items.filter((c) => c.chequeId === chequeId);
    const parsedOrder = parseParkedOrderName(cheque?.name ?? '');

    tableApi.removeItems(
      itemsOnChequeForDeletion,
      chequeId,
      reason,
      PARKED_ORDER_TABLE_ID,
      Boolean(commentOnWaste),
    );

    if (commentOnWaste !== undefined) {
      const itemsToWaste = itemsOnChequeForDeletion.map((item) => ({
        itemId: item.itemId,
        fraction: item.fraction,
        title: item.title,
        message: item.message,
      }));
      waste.register(itemsToWaste, reason, commentOnWaste);
    } else {
      /** Correction - add back inventory */
      itemsOnChequeForDeletion.forEach((item) => {
        const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

        if (invItem && typeof invItem.count === 'number') {
          inventoryApi.addStock(invItem.itemId, item.fraction);
        }
      });
    }

    if (cheque?.printed && !chequeWasSentToPOS) {
      const voidItems = itemsOnChequeForDeletion.map((item) => {
        return {
          itemId: item.itemId,
          categoryId: item.categoryId,
          title: item.title,
          fraction: item.fraction,
          price: item.amount - item.discount,
        };
      });

      sendKitchenMessage({
        type: 'VOID_CHEQUE',
        tableName: parsedOrder.orderName ?? '',
        staffName: companyStaff?.find((s) => s.id === staffId)?.name ?? '',
        items: voidItems,
        message: reason,
        isParkedOrder: true,
      });
    }

    logger.info('Staff deleted parked order', {
      cheque,
      reason,
      staffId,
    });

    const itemsTotalAmount = itemsOnChequeForDeletion.reduce(
      (acc, item) => acc + (item.amount - item.discount),
      0,
    );

    /** If we sent the cheque to POS it wasn't actually a LINE_CORRECTION */
    if (!chequeWasSentToPOS) {
      reportsApi.orderLineCorrected('REMOVE_CHEQUE', itemsTotalAmount);
    }

    tableApi.removeCheque(chequeId, PARKED_ORDER_TABLE_ID);
  };

  /**
   * Move parked order to a table
   * Real life example: This can happen if the customers start out by hanging out in the bar but would like to move to a table.
   * @param chequeId The chequeId of the parked order
   * @param tableId The tableId to move the parked order to
   */
  const moveToTable = (chequeId: string, tableId: string) => {
    const parkedOrderCheque = tableUniverse.cheques.find((c) => c.id === chequeId);

    /* Parse the order so we can remove the data we saved in the cheque name before moving the cheque to a table */
    const parsedOrder = parseParkedOrderName(parkedOrderCheque?.name ?? '');
    tableApi.setChequeName(chequeId, parsedOrder.orderName);

    setTimeout(() => {
      tableApi.moveCheque(chequeId, tableId);
    }, 1);
  };

  /**
   * Moves the parked order to payment screen
   * Logic that handles the conversion of cheque to POS order is handled in the POS screen
   * File that contains logic that continues from here: src/Scenes/POS/index.tsx
   * @param chequeId
   * @param membershipDiscountPercentage if a user is linked & has a membership discount, we need to pass it along to prevent useQuery again in POS
   */
  const toPayment = (chequeId: string, membershipDiscountPercentage?: number) => {
    if (pathname.includes('multi-pos')) {
      history.push(
        `/venue/${venueId}/multi-pos?parkedOrderId=${chequeId}&membershipDiscount=${membershipDiscountPercentage}`,
      );
      return;
    }

    history.push(
      `/venue/${venueId}/pos?parkedOrderId=${chequeId}&membershipDiscount=${membershipDiscountPercentage}`,
    );
  };

  return { park, editName, remove, moveToTable, toPayment };
};

export default useOrderParking;
